// src/components/employee/EmployeePerformance.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Calendar, Star, User, FileText, CheckCircle, Clock, Eye, Edit, Trash2, AlertCircle, TrendingUp, BarChart3 } from 'lucide-react';
import performanceService from '../../services/performance.service';
import SelfReviewModal from './SelfReviewModal';
import PerformanceAnalytics from './PerformanceAnalytics';

const EmployeePerformance = () => {
    const [activeTab, setActiveTab] = useState('reviews'); // 'reviews' or 'analytics'
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [selfReviews, setSelfReviews] = useState([]);
    const [managerReviews, setManagerReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSelfReviewModal, setShowSelfReviewModal] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [hasTemplate, setHasTemplate] = useState(false);
    const [checkingTemplate, setCheckingTemplate] = useState(false);

    useEffect(() => {
        fetchReviews();
        checkTemplate();
    }, []);

    const checkTemplate = async () => {
        try {
            setCheckingTemplate(true);
            const templateData = await performanceService.getMyTemplate();
            setHasTemplate(!!templateData);
        } catch (err) {
            console.error('Error checking template:', err);
            setHasTemplate(false);
        } finally {
            setCheckingTemplate(false);
        }
    };

    const fetchReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getMyReviews();
            setSelfReviews(data.self_reviews || []);
            setManagerReviews(data.manager_reviews || []);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Failed to load performance reviews');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSelfReview = () => {
        if (!hasTemplate) {
            alert('No performance review template assigned. Please contact your administrator to assign a template before creating a self-review.');
            return;
        }
        setSelectedReview(null);
        setModalMode('create');
        setShowSelfReviewModal(true);
    };

    const handleEditSelfReview = (review) => {
        setSelectedReview(review);
        setModalMode('edit');
        setShowSelfReviewModal(true);
    };

    const handleViewReview = (review) => {
        setSelectedReview(review);
        setModalMode('view');
        setShowSelfReviewModal(true);
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this draft?')) {
            return;
        }

        try {
            await performanceService.deleteSelfReview(reviewId);
            fetchReviews();
        } catch (err) {
            console.error('Error deleting review:', err);
            alert('Failed to delete draft');
        }
    };

    const handleModalClose = () => {
        setShowSelfReviewModal(false);
        setSelectedReview(null);
        fetchReviews();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            draft: {
                bg: 'bg-gray-100 dark:bg-gray-700',
                text: 'text-gray-800 dark:text-gray-300',
                icon: Clock,
                label: 'Draft'
            },
            submitted: {
                bg: 'bg-blue-100 dark:bg-blue-900/30',
                text: 'text-blue-800 dark:text-blue-400',
                icon: CheckCircle,
                label: 'Submitted'
            },
            under_review: {
                bg: 'bg-yellow-100 dark:bg-yellow-900/30',
                text: 'text-yellow-800 dark:text-yellow-400',
                icon: Clock,
                label: 'Under Review'
            },
            approved: {
                bg: 'bg-green-100 dark:bg-green-900/30',
                text: 'text-green-800 dark:text-green-400',
                icon: CheckCircle,
                label: 'Approved'
            },
            completed: {
                bg: 'bg-green-100 dark:bg-green-900/30',
                text: 'text-green-800 dark:text-green-400',
                icon: CheckCircle,
                label: 'Completed'
            },
            rejected: {
                bg: 'bg-red-100 dark:bg-red-900/30',
                text: 'text-red-800 dark:text-red-400',
                icon: AlertCircle,
                label: 'Needs Revision'
            }
        };

        const badge = badges[status] || badges.draft;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-3 h-3 mr-1" />
                {badge.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Performance Reviews</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Manage your self-reviews and view manager feedback
                    </p>
                </div>
                {activeTab === 'reviews' && (
                    <div className="relative">
                        <button
                            onClick={handleCreateSelfReview}
                            disabled={checkingTemplate || !hasTemplate}
                            className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                                hasTemplate
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            title={!hasTemplate ? 'No template assigned - contact administrator' : ''}
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Add Self Review
                        </button>
                        {!hasTemplate && !checkingTemplate && (
                            <p className="absolute top-full mt-1 text-xs text-red-600 dark:text-red-400 whitespace-nowrap">
                                No template assigned
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('reviews')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'reviews'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            My Reviews
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'analytics'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            Performance Analytics
                        </div>
                    </button>
                </nav>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Reviews Tab Content */}
            {activeTab === 'reviews' && (
            <>
            {/* Self Reviews Section */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-blue-600" />
                        My Self Reviews
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Self-assessments you've submitted or saved as drafts
                    </p>
                </div>

                <div className="p-6">
                    {selfReviews.length === 0 ? (
                        <div className="text-center py-12">
                            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">No self-reviews yet</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                Click "Add Self Review" to create your first self-assessment
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {selfReviews.map((review) => (
                                <div
                                    key={review.id}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {review.template_name || 'Performance Review'}
                                                </h3>
                                                {getStatusBadge(review.status)}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="flex items-center">
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                    <span>
                                                        {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                                                    </span>
                                                </div>

                                                {review.overall_rating && (
                                                    <div className="flex items-center">
                                                        <Star className="w-4 h-4 mr-2 text-yellow-400" />
                                                        <span>{parseFloat(review.overall_rating).toFixed(1)} / 5.0</span>
                                                    </div>
                                                )}

                                                {review.reviewer_first_name && (
                                                    <div className="flex items-center">
                                                        <User className="w-4 h-4 mr-2" />
                                                        <span>
                                                            Submitted to: {review.reviewer_first_name} {review.reviewer_last_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                                                {review.status === 'draft' && `Last saved: ${formatDate(review.updated_at || review.created_at)}`}
                                                {review.status === 'submitted' && `Submitted: ${formatDate(review.updated_at)}`}
                                                {review.status === 'under_review' && `Under review since: ${formatDate(review.updated_at)}`}
                                                {review.status === 'approved' && `Approved: ${formatDate(review.updated_at)}`}
                                                {review.status === 'completed' && `Completed: ${formatDate(review.updated_at)}`}
                                                {review.status === 'rejected' && `Revision requested: ${formatDate(review.updated_at)}`}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 ml-4">
                                            {review.status === 'draft' ? (
                                                <>
                                                    <button
                                                        onClick={() => handleEditSelfReview(review)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        title="Edit Draft"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteReview(review.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Delete Draft"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => handleViewReview(review)}
                                                    className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                    title="View Review"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Manager Reviews Section */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <User className="w-5 h-5 mr-2 text-green-600" />
                        Manager Reviews / Admin Confirmed Reviews
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Final performance reviews confirmed by admin
                    </p>
                </div>

                <div className="p-6">
                    {managerReviews.length === 0 ? (
                        <div className="text-center py-12">
                            <User className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">No confirmed reviews yet</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                Your final performance reviews will appear here once confirmed by admin
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {managerReviews.map((review) => (
                                <div
                                    key={review.id}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {review.template_name || 'Performance Review'}
                                                </h3>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Manager Review
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="flex items-center">
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                    <span>
                                                        {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                                                    </span>
                                                </div>

                                                {review.overall_rating && (
                                                    <div className="flex items-center">
                                                        <Star className="w-4 h-4 mr-2 text-yellow-400" />
                                                        <span className="font-medium">{parseFloat(review.overall_rating).toFixed(1)} / 5.0</span>
                                                    </div>
                                                )}

                                                {review.reviewer_first_name && (
                                                    <div className="flex items-center">
                                                        <User className="w-4 h-4 mr-2" />
                                                        <span>
                                                            By: {review.reviewer_first_name} {review.reviewer_last_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {review.strengths && (
                                                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg">
                                                    <p className="text-xs font-semibold text-green-800 dark:text-green-400 mb-1">Strengths</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300">{review.strengths}</p>
                                                </div>
                                            )}

                                            {review.areas_for_improvement && (
                                                <div className="mt-2 p-3 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                                                    <p className="text-xs font-semibold text-orange-800 dark:text-orange-400 mb-1">Areas for Improvement</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300">{review.areas_for_improvement}</p>
                                                </div>
                                            )}

                                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                                                Reviewed: {formatDate(review.created_at)}
                                            </div>
                                        </div>

                                        <div className="ml-4">
                                            <button
                                                onClick={() => handleViewReview(review)}
                                                className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="View Full Review"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            </>
            )}

            {/* Analytics Tab Content */}
            {activeTab === 'analytics' && (
                <PerformanceAnalytics />
            )}

            {/* Self Review Modal */}
            {showSelfReviewModal && (
                <SelfReviewModal
                    isOpen={showSelfReviewModal}
                    onClose={handleModalClose}
                    review={selectedReview}
                    mode={modalMode}
                />
            )}
        </div>
    );
};

export default EmployeePerformance;
